import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Box, Stack } from '@mui/material'
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { DynamicFlowStepper, DynamicFlowView, useDynamicFlowController } from 'components/common/DynamicFlow'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { JobPollErrorType, JobType, useJobPoll } from 'dataQueries'
import { UploadVisualsTypes, useClientPropertySelfUploadPopup } from './ClientPropertySelfUploadPopup.context'

import { MUIButton } from 'components/common/MUIButton'
import Modal from 'components/common/Modals/Modal/Modal'
import { Path } from 'constants/router'
import { UploadOptionsStep } from './UploadOptionsStep.component'
import { UploadVisualsStep } from './UploadVisualsStep.component'
import { VisualFileType } from 'constants/visual'
import { invalidateListVisuals } from 'dataQueries/visual.query'
import { useClientProperty } from '../_main/contexts'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { useSelfUpload } from 'dataQueries/selfUpload.query'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { logger } from 'utils/logging'

/** Enum of all upload visuals steps */
export enum UploadVisualsStepKey {
  UPLOAD_PHOTOS = 'UPLOAD_PHOTOS',
  OPTIONS = 'OPTIONS',
}

/**
 * @interface Props
 */
interface Props {
  /** Whether the upload popup is opened */
  isUploadPopupOpened: boolean
  /** Function to set the upload popup state */
  setIsUploadPopupOpened: Dispatch<SetStateAction<boolean>>
  /** Normalized original type of the visual */
  normalizedOriginalType: VisualFileType
}

/**
 * @component ClientPropertySelfUploadPopupController 
 * @example
 * <ClientPropertySelfUploadPopupController />
 */

export const ClientPropertySelfUploadPopupController: FC<Props> = ({
  isUploadPopupOpened,
  setIsUploadPopupOpened,
  normalizedOriginalType
}) => {

  const { t } = useTranslation(['self_upload_popup', 'gallery', 'common'])
  const { spawnWarningToast, spawnErrorToast } = useSnackbar()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const flowCtrl = useDynamicFlowController()
  const { userPropertyWorkspaceId, baseUserData } = useUserData()
  const { visualsCount, uploadVisualsCount } = useClientPropertySelfUploadPopup()

  const {
    propertyData,
    propertyId,
    sessionId,
    assignmentId,
    getCategoryConfig
  } = useClientProperty()

  const {
    setSelectedSkyReplacement,
    setIsSkyReplacementEnabled,
    setUploadVisualsType,
    uploadVisualsType,
    isSkyReplacementEnabled,
    selectedSkyReplacement
  } = useClientPropertySelfUploadPopup()

  const selfUploadVisuals = useSelfUpload()
  const jobPoll = useJobPoll(JobType.SELF_UPLOAD)

  const isLastStep = useMemo(() => !flowCtrl.currentNode?.next, [flowCtrl.currentNode?.next])

  const [isSubmitLoading, setIsSubmitLoading] = useState(false)

  const onClose = useCallback(() => {
    setIsUploadPopupOpened(false)
    setSelectedSkyReplacement(null)
    setIsSkyReplacementEnabled(false)
    setUploadVisualsType(UploadVisualsTypes.ORIGINAL_STATE)
    setIsSubmitLoading(false)
    flowCtrl.setHead(UploadVisualsStepKey.UPLOAD_PHOTOS)
    getCategoryConfig.mutate({ email: baseUserData?.email || '' })
  }, [baseUserData?.email, flowCtrl, getCategoryConfig, setIsSkyReplacementEnabled, setIsUploadPopupOpened, setSelectedSkyReplacement, setUploadVisualsType])

  const onSubmit = useCallback(() => {
    if (!sessionId) {
      // The drops a moment earlier needed this same sessionId, so record what the category-config mutation is doing now that it is gone
      logger.warn('Self-upload submit blocked: sessionId missing', null, {
        context: {
          component: 'ClientPropertySelfUploadPopupController',
          action: 'submit',
          metadata: { categoryConfigStatus: getCategoryConfig.status, hasCategoryConfigData: !!getCategoryConfig.data, visualsCount, propertyId, assignmentId },
        },
      })
      spawnWarningToast(t('gallery:open_self_upload_error'))
      return
    }

    setIsSubmitLoading(true)

    const isAiEditingRequest = uploadVisualsType === UploadVisualsTypes.AI_EDITING

    selfUploadVisuals.mutate(
      {
        workspaceId: userPropertyWorkspaceId,
        realtyId: propertyId,
        sessionId,
        aiEditingRequest: isAiEditingRequest ?
          {
            skyReplacement: isSkyReplacementEnabled,
            cloudType: selectedSkyReplacement,
          }
          :
          undefined,
      },
      {
        onSuccess: async (data) => {
          const { jobId, assignmentId: createdAssignmentId } = data.data

          try {
            await jobPoll.mutateAsync({ jobId })
          } catch (e) {
            const errorType = (e as { response?: { data?: { errorType?: JobPollErrorType } } })?.response?.data?.errorType

            if (!errorType) {
              logger.error('Self-upload job poll failed outside the JobPoll error contract', null, {
                context: {
                  component: 'ClientPropertySelfUploadPopupController',
                  action: 'submit',
                  metadata: { jobId, propertyId, assignmentId: createdAssignmentId, reason: e instanceof Error ? e.message : String(e) },
                },
              })
            }

            // The POST already created the assignment whatever the poll says, so re-arming submit would create a second one
            if (errorType === JobPollErrorType.POLLING_RETRY_LIMIT) spawnWarningToast(t('upload_still_processing'))
            else spawnErrorToast(t('common:please_try_later'))
          }

          await queryClient.invalidateQueries({
            queryKey: [EntityKeys.PROPERTY_ASSIGNMENTS, QueryType.LIST, { propertyId, workspaceId: userPropertyWorkspaceId }],
          })

          setTimeout(() => {
            onClose()
            if (assignmentId !== createdAssignmentId) {
              navigate(Path.PROPERTY_VISUALS_GALLERY_ASSIGNMENT.replace(':id', propertyId.toString()).replace(':assignmentId', createdAssignmentId))
            } else {
              invalidateListVisuals(queryClient, assignmentId)
            }
          }, 3000)
        },
        onError(_) {
          setIsSubmitLoading(false)
          spawnErrorToast(t('common:please_try_later'))
        }
      }
    )
  }, [assignmentId, getCategoryConfig.data, getCategoryConfig.status, queryClient, isSkyReplacementEnabled, jobPoll, navigate, onClose, propertyId, selectedSkyReplacement, selfUploadVisuals, sessionId, spawnErrorToast, spawnWarningToast, t, uploadVisualsType, userPropertyWorkspaceId, visualsCount])

  useEffect(() => {
    flowCtrl.createEntryNode(
      UploadVisualsStepKey.UPLOAD_PHOTOS,
      {
        component: <UploadVisualsStep />,
        hasFooter: true,
        type: 'main',
      }
    )

    flowCtrl.insertAfter(
      UploadVisualsStepKey.UPLOAD_PHOTOS,
      UploadVisualsStepKey.OPTIONS,
      {
        component: <UploadOptionsStep />,
        hasFooter: true,
        type: 'main',
      }
    )
     
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const disabledSubmit = useMemo(() => {
    if (uploadVisualsType === UploadVisualsTypes.AI_EDITING) {
      return isSkyReplacementEnabled && !selectedSkyReplacement
    }

    return false
  }, [isSkyReplacementEnabled, selectedSkyReplacement, uploadVisualsType])

  return (
    <Modal
      isOpen={isUploadPopupOpened}
      onClose={() => !isSubmitLoading && onClose()}
      onClickOutside={() => !isSubmitLoading && onClose()}
      hasHeaderContentDivider
      contentStyle={{
        width: '80rem'
      }}
      extraHeaderContent={
        <Box width="50%">
          <DynamicFlowStepper stepNameTranslatorFnc={(key) => t(`steps.${key}`)} />
        </Box>
      }
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap={2}>

          <MUIButton
            disabled={isSubmitLoading}
            type="secondaryBorder"
            onClick={onClose}
          >
            {t('Cancel')}
          </MUIButton>

          {!!flowCtrl.currentNode?.prev &&
            <MUIButton
              disabled={isSubmitLoading}
              type="secondaryBorder"
              onClick={() => { flowCtrl.setHead(flowCtrl.currentNode!.prev!) }}
            >
              {t('common:Previous')}
            </MUIButton>
          }

          {flowCtrl.currentNode?.key === UploadVisualsStepKey.UPLOAD_PHOTOS &&
            <MUIButton
              disabled={visualsCount === 0 || uploadVisualsCount !== 0}
              onClick={() => {
                if (visualsCount > 0) flowCtrl.setHead(flowCtrl.currentNode!.next!)

                logAnalyticsEvent(AnalyticsEvent.SELF_UPLOAD_NEXT_STEP, {
                  propertyName: propertyData?.title,
                  propertyId,
                  visualsUploaded: visualsCount,
                })
              }}
            >
              {t('next_step')}
            </MUIButton>
          }

          {isLastStep &&
            <MUIButton
              disabled={disabledSubmit}
              isLoading={isSubmitLoading}
              onClick={onSubmit}
            >
              {t('upload_photos')}
            </MUIButton>
          }

        </Stack>
      }
    >
      <Stack
        justifyContent="flex-start"
        width="100%"
        height="100%"
        overflow="auto"
        position="relative"
      >
        <Stack gap={2}>
          <DynamicFlowView />
        </Stack>
      </Stack>
    </Modal>
  )
}
